import type { UseQueryResult } from "@tanstack/react-query";
import type { BouncerRow } from "@/api/client.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.js";

type Props = {
  query: UseQueryResult<BouncerRow[], Error>;
};

export function BouncersCard({ query }: Props) {
  const { data, isLoading, isError, error } = query;

  return (
    <Card data-testid="bouncers-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Bouncers</CardTitle>
        <CardDescription>Remediation components registered with LAPI (read-only).</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : null}
        {isError ? (
          <p role="alert" className="text-sm text-destructive">
            {error?.message ?? "Failed to load bouncers"}
          </p>
        ) : null}
        {!isLoading && !isError && data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No bouncers returned.</p>
        ) : null}
        {!isLoading && !isError && data && data.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Last pull</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((b) => (
                <TableRow key={b.name}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell>{b.type ?? "—"}</TableCell>
                  <TableCell className="max-w-[12rem] truncate">{b.version ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{b.lastPull ?? b.updatedAt ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
